import React, { useEffect, useState, useReducer, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Input, Button, Select, Row, Col, message,
   Modal, DatePicker, Upload, Form, Tree, InputNumber, Typography, Switch
} from 'antd';
import * as AntDIcons from '@ant-design/icons';
import SimpleBarReact from "simplebar-react";
import axios from 'axios';
import uuid from 'react-uuid';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import StickyBox from "react-sticky-box";
import moment from 'moment';
import Wysiwyg2 from '../../../components/wysiwyg/wysiwyg2';
import WysiwygMathEditor from '../../../components/wysiwyg/WysiwygMathEditor';
import { allowed_levels_in_microcredentials } from '../../../constants';
import { getCourses, getTopicsByCourse } from '../../../services/MicrocredentialsCourseTopicService';
import { buildMicrocredentialSubjectCode, getMicrocredentialCourseId, getMicrocredentialCourseName, getMicrocredentialSubjectIdValue, getMicrocredentialTopicId, getMicrocredentialTopicName } from '../microcredentialUtils';
import { isPlagiarismEligibleLevel } from '../plagiarismUtils';

const { Option } = Select;
const { Text } = Typography;

export default function Create(props) {
   const [form] = Form.useForm();
   const editorRef = useRef(null);
   const navigate = useNavigate();

   const [subjectList, setSubjectList] = useState([]);
   const [sectionList, setSectionList] = useState([]);
   const [levelList, setLevelList] = useState([]);
   const [secsubCreateLoading, setSecSubCreateLoading] = useState(false);
   const [homeworkTitle, setHomeworkTitle] = useState("");
   const [levelCreate, setLevelCreate] = useState(null);
   const [sectionCreate, setSectionCreate] = useState([]);
   const [subjectCreate, setSubjectCreate] = useState(null);
   const [descriptionCreate, setDescriptionCreate] = useState(null);
   const [uploadFileList, setUploadFileList] = useState([]);
   const [subjectCreateList, setSubjectCreateList] = useState([]);
   const [sectionCreateList, setSectionCreateList] = useState([]);
   const [homeworkDate, setHomeworkDate] = useState('');
   const [submissionDate, setSubmissionDate] = useState('');
   const [maxScore, setMaxScore] = useState(null);
   const [topicCreate, setTopicCreate] = useState(null);
   const [topicCreateList, setTopicCreateList] = useState([]);
   const [coursesLoading, setCoursesLoading] = useState(false);
   const [topicsLoading, setTopicsLoading] = useState(false);

   const [checkedKeys, setCheckedKeys] = useState([]);
   const [selectedKeys, setSelectedKeys] = useState([]);
   const [treeData, setTreeData] = useState([]);
   const [expandedKeys, setExpandedKeys] = useState([]);
   const [assignedStudents, setAssignedStudents] = useState(null);
   const [assignedStudentsWithSection, setAssignedStudentsWithSection] = useState(null);

   

   const user_id = Utils.getCurrentUserInfo(Enums.UserInfo.id);
   const user_role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
   const isMicrocredentialType = props.type === 'microcredential';

   // const dateFormat = 'MMM D, YYYY';
   // const customFormat = value => `${value.format(dateFormat)}`;

   const dateFormat = 'MMM D, YYYY h:mm a';
   const customFormat = value => `${value.format(dateFormat)}`;

   useEffect(() => {
      Utils.verifyJwt();

      // if (user_role.toLowerCase() !== 'admin' && user_role.toLowerCase() !== 'teacher' && user_role.toLowerCase() !== 'co-teacher')
      const allowedRoles = ['super admin', 'admin', 'teacher', 'co-teacher'];
      if (!allowedRoles.includes(user_role.toLowerCase()))
         navigate('/home');

      getLevels();

      if (isMicrocredentialType) {
         loadMicrocredentialCourses();
      }
   }, []);

   const getLevels = () => {
      axios.get(`${process.env.REACT_APP_API_LOOKUP}/levellist`).then((response) => {
         if (response.data) {
            const data = response.data;
            const processedLevels = data.map(item => ({ 
               value: item.value, 
               name: item.name 
            })).filter(item => {
               if (!isMicrocredentialType) return true;

               const val = String(item.value ?? '').trim();
               const label = String(item.name ?? '').toLowerCase();
                 
               return allowed_levels_in_microcredentials.some(lvl => val === lvl || label.includes(lvl));
            });

            setLevelList(processedLevels);
         }
      }, (error) => {
         console.log(error.message);
      });
   }

   const loadStudentTree = (levelValue) => {
      axios.get(`${process.env.REACT_APP_API_ASSESSMENT}/studentlistperleveljson?level=${levelValue}`).then((response) => {
         if (response.data) {
            const data = response.data;
            setTreeData(data);
            setCheckedKeys([]);
            setExpandedKeys([]);
         }

         setSecSubCreateLoading(false);
      }, (error) => {
         console.log(error.message);
         setTreeData([]);
         setCheckedKeys([]);
         setExpandedKeys([]);
         setSecSubCreateLoading(false);
      });
   };

   const loadMicrocredentialCourses = async () => {
      try {
         setCoursesLoading(true);
         const data = await getCourses();
         const courses = (Array.isArray(data) ? data : []).map((course) => ({
            value: getMicrocredentialCourseId(course),
            name: getMicrocredentialCourseName(course),
         })).filter((course) => course.value !== null && course.name);

         setSubjectCreateList(courses);
      } catch (error) {
         message.error('Failed to load courses');
         setSubjectCreateList([]);
      } finally {
         setCoursesLoading(false);
      }
   };

   const loadMicrocredentialTopics = async (courseId) => {
      if (!courseId) {
         setTopicCreateList([]);
         return;
      }

      try {
         setTopicsLoading(true);
         const data = await getTopicsByCourse(courseId);
         const topics = (Array.isArray(data) ? data : []).map((topic) => ({
            value: getMicrocredentialTopicId(topic),
            name: getMicrocredentialTopicName(topic),
         })).filter((topic) => topic.value !== null && topic.name);

         setTopicCreateList(topics);
      } catch (error) {
         message.error('Failed to load topics');
         setTopicCreateList([]);
      } finally {
         setTopicsLoading(false);
      }
   };

   const handleCourseCreateChange = (courseId) => {
      setSubjectCreate(courseId);
      setTopicCreate(null);
      form.setFieldsValue({ course_topic: undefined });

      if (courseId) {
         loadMicrocredentialTopics(courseId);
      } else {
         setTopicCreateList([]);
      }
   };

   const upload_props = {
      onRemove: file => {
         setUploadFileList(() => {
            const index = uploadFileList.indexOf(file.originFileObj);
            const newFileList = uploadFileList.slice();
            newFileList.splice(index, 1);
            return newFileList;
         })
      },

      beforeUpload: file => {
         const isPNG = file.type === 'image/png';
         const isJPG = file.type === 'image/jpeg';
         const isBMP = file.type === 'image/bmp';
         const isDOC = file.type === 'application/msword' || file.type === 'application/vnd.ms-word';
         const isDOCX = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
         const isXLS = file.type === 'application/msexcel' || file.type === 'application/vnd.ms-excel';
         const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
         const isPPT = file.type === 'application/vnd.ms-powerpoint';
         const isPPTX = file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
         const isPPSX = file.type === 'application/vnd.openxmlformats-officedocument.presentationml.slideshow';
         const isPDF = file.type === 'application/pdf';

         const fileValid = isPNG || isJPG || isBMP || isDOC || isDOCX || isXLS || isXLSX || isPPT || isPPTX || isPDF || isPPSX;

         const allowedFileSize = 50;

         if (fileValid) {
            const isLtMB = file.size / 1024 / 1024 <= allowedFileSize;

            if (isLtMB) {
               var list = uploadFileList;
               list.push(file);
               setUploadFileList(list);
            } else {
               Modal.error({
                  title: 'Homework',
                  content: `Maximum allowed file size is ${allowedFileSize} MB`,
                  centered: true
               });
            }

            // console.log(uploadFileList);
         } else {
            Modal.error({
               title: 'Homework',
               content: 'You are not allowed to upload that file type',
               centered: true
            });
         }

         return false;
      },

      onChange(info) {
         if (info.file.status !== 'uploading') {
            console.log(info.file, info.fileList);
         }
         if (info.file.status === 'done') {
            message.success(`${info.file.name} file uploaded successfully`);
         } else if (info.file.status === 'error') {
            message.error(`${info.file.name} file upload failed.`);
         }
      },

      progress: {
         strokeColor: {
            '0%': '#005ff9',
            '100%': '#87d068',
         },
         strokeWidth: 3,
         format: percent => percent && `${parseFloat(percent.toFixed(2))}%`,
      },

      uploadFileList,
   };

   const handleLevelCreateChange = (val) => {
      setSectionCreate([]);
      setSubjectCreate(null);
      setTopicCreate(null);
      setTopicCreateList([]);
      setLevelCreate(val);
      if (!isPlagiarismEligibleLevel(val, levelList))
         form.setFieldsValue({ plagiarism_checker: false });
      setSecSubCreateLoading(true);

      if (isMicrocredentialType) {
         loadStudentTree(val);
         return;
      }

      const subjectlist = [];
      axios.get(`${process.env.REACT_APP_API_LOOKUP}/subjectlist?level=${val}&school_year=-1`).then((response) => {
         if (response.data) {
            const data = response.data;

            data.map((item) => {
               let obj = { 'value': item.value, 'name': item.name }
               subjectlist.push(obj);
            });

            setSubjectCreateList(subjectlist);
            loadStudentTree(val);
         }
      }, (error) => {
         console.log(error.message);
         setSecSubCreateLoading(false);
      });
   }

   const handleDescriptionChange = (val) => {
      setDescriptionCreate(val);
   }

   const handleFormValuesChange = (changedValues) => {
      const formFieldName = Object.keys(changedValues)[0];

      if (formFieldName === "level") {
         setLevelCreate(changedValues[formFieldName]);
         form.setFieldsValue({ section: undefined });
         form.setFieldsValue({ subject: undefined });
         setSubjectList([]);
         setSectionList([]);
         setAssignedStudents([]);
         setAssignedStudentsWithSection([]);
         if (!isPlagiarismEligibleLevel(changedValues[formFieldName], levelList))
            form.setFieldsValue({ plagiarism_checker: false });

         if (isMicrocredentialType) {
            form.setFieldsValue({ course_topic: undefined });
            setTopicCreate(null);
            setTopicCreateList([]);
         }
      }
   };

   const handleCancelCreate = () => {
      setUploadFileList([]);
      setSectionCreate([]);
      setSubjectCreate(null);
      setTopicCreate(null);
      setTopicCreateList([]);
      setLevelCreate(null);
      form.resetFields();
      props.onCancelCallback();
   }

   const onFinish = (values) => {
      let user = Utils.getCurrentUserInfo(Enums.UserInfo.id)
      var o2o_mode = Utils.determineOnlineOffline();
      var homework_id = "homework_" + o2o_mode + "_" + uuid();
      const selectedCourseId = values.subject ?? subjectCreate;
      const selectedTopicId = values.course_topic ?? topicCreate;
      const selectedCourse = subjectCreateList.find(({ value }) => String(value) === String(selectedCourseId));
      const selectedTopic = topicCreateList.find(({ value }) => String(value) === String(selectedTopicId));
      const subjectId = isMicrocredentialType ? getMicrocredentialSubjectIdValue(selectedCourseId) : selectedCourseId;

      let homeWorkData = {
         id: homework_id,
         class_id: levelCreate,
         session_id: 0,
         title: homeworkTitle,
         homework_date: moment(homeworkDate).format('YYYY-MM-DD HH:mm:ss'),
         submit_date: moment(submissionDate).format('YYYY-MM-DD HH:mm:ss'),
         subject_id: subjectId,
         description: descriptionCreate,
         created_by: user,
         document: '',
         sections: '',
         assigned_students: assignedStudentsWithSection,
         students: assignedStudents,
         max_points: maxScore,
         type: props.type,
         plagiarism_checker: isPlagiarismEligibleLevel(levelCreate, levelList)
            && values.plagiarism_checker === true,
         ...(isMicrocredentialType ? {
            subject_code: buildMicrocredentialSubjectCode({
               courseId: selectedCourseId,
               courseName: selectedCourse?.name ?? '',
               topicId: selectedTopicId,
               topicTitle: selectedTopic?.name ?? '',
            }),
         } : {}),
      }

      props.onFinishCallback(homeWorkData, uploadFileList)
   }

   const onExpand = (expandedKeysValue) => {
      setExpandedKeys(expandedKeysValue);
   };

   const onCheck = (checkedKeysValue) => {
      setCheckedKeys(checkedKeysValue);

      //-- (start) with section ------------------------------
      const selectedStudsWithSection = checkedKeysValue.filter((e) => {
         return e.includes('_');
         //return e.includes('|');
      });

      setAssignedStudentsWithSection(selectedStudsWithSection.length > 0 ? selectedStudsWithSection.join(',') : '');
      //-- (end) with section ------------------------------

      if (selectedStudsWithSection.length > 0) {
         //-- Remove the section part
         for (var i = 0; i < selectedStudsWithSection.length; i++) {
            selectedStudsWithSection[i] = selectedStudsWithSection[i].substring(selectedStudsWithSection[i].indexOf('_') + 1, selectedStudsWithSection[i].length);
            //selectedStudsWithSection[i] = selectedStudsWithSection[i].substring(selectedStudsWithSection[i].indexOf('|') + 1, selectedStudsWithSection[i].length);
         }

         var selected = [...new Set(selectedStudsWithSection)]; //-- Remove duplicate students
         setAssignedStudents(selected.join(','));
      } else {
         setAssignedStudents('');
      }
   };

   const onSelect = (selectedKeysValue, info) => {
      setSelectedKeys(selectedKeysValue);
   };

   const selectedStudentCount = checkedKeys.filter((key) => key.includes('_')).length;
   const plagiarismEligible = isPlagiarismEligibleLevel(levelCreate, levelList);
   const workTypeLabel = props.type === "project"
      ? "Project"
      : props.type === "microcredential"
         ? "Microcredential H&P"
         : "Homework";

   return (
      <>
         <Form
            id={props.formId || "homework-assignment-create-form"}
            className="homework-modal-form"
            form={form}
            layout={"vertical"}
            colon={false}
            onFinish={onFinish}
            onValuesChange={handleFormValuesChange}
         >
            <div className="homework-modal-section">
               <div className="homework-modal-section-header">
                  <span className="homework-modal-section-icon"><AntDIcons.SettingOutlined /></span>
                  <div>
                     <span className="homework-modal-kicker">Details</span>
                     <h3>{workTypeLabel} Settings</h3>
                  </div>
               </div>
               <Row gutter={16}>
                  <Col xs={24}>
                  <Form.Item name="title" label={<Text>Title</Text>}
                     rules={[{
                        required: true,
                        message: 'Title should not be blank'
                     }]}
                  >
                     <Input
                        allowClear
                        size='middle'
                        style={{ borderRadius: 7 }}
                        placeholder={`Enter ${workTypeLabel.toLowerCase()} title`}
                        onBlur={(event) => setHomeworkTitle(event.target.value)}
                     />
                  </Form.Item>
                  </Col>
               </Row>

               <Row gutter={16}>
                  <Col xs={24} md={isMicrocredentialType ? 5 : 6} lg={isMicrocredentialType ? 5 : 6}>
                  <Form.Item name="level" label={<Text>Level</Text>}
                     rules={[{
                        required: true,
                        message: 'Please select level'
                     }]}
                  >
                     <Select
                        showArrow
                        size='middle'
                        style={{ width: '100%' }}
                        allowClear
                        placeholder="Select Level"
                        onChange={(val) => handleLevelCreateChange(val)}
                     >
                        {levelList.map(({ value, name }) => {
                           return <Option key={`level-${value}`} value={value}>{name}</Option>
                        })}
                     </Select>
                  </Form.Item>
                  </Col>
                  <Col xs={24} md={isMicrocredentialType ? 7 : 12} lg={isMicrocredentialType ? 7 : 12}>
                  <Form.Item name="subject" label={<Text>{isMicrocredentialType ? 'Course' : 'Subject'}</Text>}
                     rules={[{
                        required: true,
                        message: isMicrocredentialType ? 'Please select course' : 'Please select subject'
                     }]}
                  >
                     <Select
                        loading={isMicrocredentialType ? coursesLoading : secsubCreateLoading}
                        showArrow
                        size='middle'
                        style={{ width: '100%' }}
                        allowClear
                        placeholder={isMicrocredentialType ? 'Select Course' : 'Select Subject'}
                        value={subjectCreate}
                        onChange={(val) => isMicrocredentialType ? handleCourseCreateChange(val) : setSubjectCreate(val)}
                     >
                        {subjectCreateList.map(({ value, name }) => {
                           return <Option key={`subject-${value}`} value={value}>{name}</Option>
                        })}
                     </Select>
                  </Form.Item>
                  </Col>
                  {isMicrocredentialType && (
                     <Col xs={24} md={7} lg={7}>
                        <Form.Item
                           name="course_topic"
                           label={<Text>Topic</Text>}
                           rules={[{
                              required: true,
                              message: 'Please select topic',
                           }]}
                        >
                           <Select
                              loading={topicsLoading}
                              showArrow
                              size='middle'
                              style={{ width: '100%' }}
                              allowClear
                              placeholder="Select Topic"
                              value={topicCreate}
                              disabled={!subjectCreate}
                              onChange={(val) => setTopicCreate(val)}
                           >
                              {topicCreateList.map(({ value, name }) => (
                                 <Option key={`topic-${value}`} value={value}>{name}</Option>
                              ))}
                           </Select>
                        </Form.Item>
                     </Col>
                  )}
                  <Col xs={24} md={isMicrocredentialType ? 5 : 6} lg={isMicrocredentialType ? 5 : 6}>
                  <Form.Item name="max_score" label={<Text>Max Score</Text>}
                     rules={[{
                        required: true,
                        message: 'Please Enter Max Score'
                     }]}
                  >
                     <InputNumber
                        allowClear
                        min={0}
                        max={100}
                        size='middle'
                        style={{ borderRadius: 7, width: '100%' }}
                        placeholder="Enter max score"
                        onBlur={(event) => setMaxScore(event.target.value)}
                     />
                  </Form.Item>
                  </Col>
               </Row>
               <Row gutter={16} align="top">
                  <Col xs={24} md={8} lg={8}>
                  <Form.Item name="homework_date" label={<Text>{workTypeLabel} Date</Text>}
                     rules={[{
                        required: true,
                        message: `Please enter ${workTypeLabel.toLowerCase()} date`
                     }]}
                  >
                     <DatePicker
                        size='middle'
                        showTime={{ format: 'hh:mm A', use12Hours: true }}
                        format={customFormat}
                        style={{ width: "100%" }}
                        onBlur={(event) => setHomeworkDate(event.target.value)}
                     />
                  </Form.Item>
                  </Col>
                  <Col xs={24} md={8} lg={8}>
                  <Form.Item name="submission_date" label={<Text>Submission Date</Text>}
                     rules={[{
                        required: true,
                        message: 'Please enter submission date'
                     }]}
                  >
                     <DatePicker
                        size='middle'
                        showTime={{ format: 'hh:mm A', use12Hours: true }}
                        style={{ width: "100%" }}
                        onBlur={(event) => setSubmissionDate(event.target.value)}
                        format={customFormat}
                     />
                  </Form.Item>
                  </Col>
                  <Col xs={24} md={8} lg={8}>
                        <Form.Item
                           name="plagiarism_checker"
                           label={<Text>Plagiarism Checker</Text>}
                           valuePropName="checked"
                           initialValue={false}
                        >
                           <Switch
                              disabled={!plagiarismEligible}
                              checkedChildren="Enabled"
                              unCheckedChildren="Off"
                           />
                        </Form.Item>
                        {/* <Text type="secondary">
                           {plagiarismEligible
                              ? 'Enable plagiarism scanning for student file submissions.'
                              : 'Available only for Grade 9 to Grade 12.'}
                        </Text> */}
                  </Col>
               </Row>
            </div>

            <Row gutter={16} className="homework-modal-panel-grid">
               <Col xs={24} md={8} lg={8}>
                  <div className="homework-modal-panel">
                     <div className="homework-modal-section-header">
                        <span className="homework-modal-section-icon"><AntDIcons.UsergroupAddOutlined /></span>
                        <div>
                           <span className="homework-modal-kicker">Recipients</span>
                           <h3>Select Students</h3>
                        </div>
                        <span className="homework-modal-count">{selectedStudentCount}</span>
                     </div>
                     <Form.Item name="select_students" label={<Text>Select Students</Text>} style={{ marginBottom: '5px' }}
                  // rules={[{
                  //    required: true,
                  //    message: 'Please select students'
                  // }]}
                     >
                     <SimpleBarReact className="homework-modal-scroll">
                        <div className="student-div homework-student-tree">
                           <Tree
                              checkable
                              onExpand={onExpand}
                              autoExpandParent={true}
                              onCheck={onCheck}
                              checkedKeys={checkedKeys}
                              onSelect={onSelect}
                              selectedKeys={selectedKeys}
                              treeData={treeData}
                              style={{ paddingBottom: "15px", paddingTop: "15px" }}
                              defaultExpandParent={true}
                           />
                        </div>
                     </SimpleBarReact>
                     </Form.Item>
                  </div>
               </Col>
               <Col xs={24} md={8} lg={8}>
                  <div className="homework-modal-panel">
                     <div className="homework-modal-section-header">
                        <span className="homework-modal-section-icon"><AntDIcons.FileTextOutlined /></span>
                        <div>
                           <span className="homework-modal-kicker">Content</span>
                           <h3>Description</h3>
                        </div>
                     </div>
                  {/* <Wysiwyg2
                     changeContentCallback={(e) => handleDescriptionChange(e)}
                     content={''}
                     height={250}
                     contentHeight={135}
                  /> */}
                  <WysiwygMathEditor
                     id={`homework-body-${user_id}`}
                     content={''}
                     changeContentCallback={(e) => handleDescriptionChange(e)}
                     height={260}
                     // contentHeight={230}
                     disabled={false}
                  />
                  {/* <Editor
                     apiKey={`${process.env.REACT_APP_TINY_MCE_API_KEY}`}
                     onInit={(evt, editor) => editorRef.current = editor}
                     onFocusOut={(e) => handleDescriptionChange(e.target.innerHTML)}
                     initialValue=""
                     init={{
                        menubar: false,
                        height: 250,
                        statusbar: true,
                        plugins: [
                           'advlist autolink lists link image charmap print preview anchor',
                           'searchreplace visualblocks code',
                           'insertdatetime media table paste code help wordcount emoticons'
                        ],
                        toolbar: 'undo redo | formatselect | ' +
                           'bold italic backcolor | alignleft aligncenter ' +
                           'alignright alignjustify | bullist numlist outdent indent | ' +
                           'removeformat | emoticons | help',
                          content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                       }}
                  /> */}
                  </div>
               </Col>
               <Col xs={24} md={8} lg={8}>
                  <div className="homework-modal-panel">
                     <div className="homework-modal-section-header">
                        <span className="homework-modal-section-icon"><AntDIcons.CloudUploadOutlined /></span>
                        <div>
                           <span className="homework-modal-kicker">Resources</span>
                           <h3>Attach Documents</h3>
                        </div>
                     </div>
                     <Form.Item name="attached_documents" label={<Text>Attach Document(s)</Text>} style={{ marginBottom: '5px' }}>
                     <SimpleBarReact className="homework-modal-scroll">
                        <StickyBox>
                           <Upload
                              style={{ display: "block" }}
                              maxCount={3}
                              multiple
                              {...upload_props}
                              listType="picture"
                              defaultFileList={[...uploadFileList]}
                              className="upload-list-inline"
                              accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                .xls,.xlsx,.png,.jpg,.pdf,.ppsx"
                           >
                              <Button size='middle' shape='round' icon={<AntDIcons.CloudUploadOutlined />} block>Select File</Button>
                           </Upload>
                        </StickyBox>
                     </SimpleBarReact>
                     </Form.Item>
                  </div>
               </Col>
            </Row>

            {/* <Row gutter={24} >
               <Col span={24}>
                  <div className='input-columns'>
                     <label>Description</label>
                     <Editor
                        apiKey={`${process.env.REACT_APP_TINY_MCE_API_KEY}`}
                        onInit={(evt, editor) => editorRef.current = editor}
                        onFocusOut={(e) => handleDescriptionChange(e.target.innerHTML)}
                        initialValue=""
                        init={{
                           menubar: true,
                           height: 250,
                           statusbar: true,
                           plugins: [
                              'advlist autolink lists link image charmap print preview anchor',
                              'searchreplace visualblocks code',
                              'insertdatetime media table paste code help wordcount emoticons'
                           ],
                           toolbar: 'undo redo | formatselect | ' +
                              'bold italic backcolor | alignleft aligncenter ' +
                              'alignright alignjustify | bullist numlist outdent indent | ' +
                              'removeformat | emoticons | help',
                           content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                        }}
                     />

                     <Form.Item name="attached_documents" label="Attach Document(s)">
                        <SimpleBarReact style={{ maxHeight: "250px", overflow: "auto" }}>
                           <StickyBox style={{ zIndex: 1000 }}>
                              <Upload
                                 style={{ display: "block" }}
                                 maxCount={3}
                                 multiple
                                 {...upload_props}
                                 listType="picture"
                                 defaultFileList={[...uploadFileList]}
                                 className="upload-list-inline"
                                 accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                .xls,.xlsx,.png,.jpg,.pdf,.ppsx"
                              >
                                 <Button size='middle' shape='round' icon={<AntDIcons.UploadOutlined />} block>Select File</Button>
                              </Upload>
                           </StickyBox>
                        </SimpleBarReact>
                     </Form.Item>
                  </div>
               </Col>
            </Row> */}
         </Form>
      </>
   );
}

